import PropTypes from "prop-types"
import { useT } from "react-polyglot-hooks"
import Tag from "components/styled/Tag"
import styled from "styled-components"

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;

  /* This element will push the flash sale icon if copy gets too long */
  max-width: 100%;

  .car-item:not(.no-promo) & {
    max-width: calc(100% - 135px);
  }

  > ${Tag} {
    display: block;
    max-width: 100%;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;

    &:nth-child(n + 2) {
      margin-top: 5px;
    }
  }
`

export default function TagsList({
  limit,
  sold,
  lastItems,
  fastDelivering,
  sellingStatus,
  parcel,
  isothermal,
  availableForCompany,
  availableForFreelance,
  availableForEmployee,
  reverse,
  noFastDelivering,
  flexibleStatus, hasAllRiskInsurance
}) {
  const t = useT()

  if (sold)
    return (
      <Wrapper>
        <Tag key="sold" color="#e81e61">
          {t("car.tags.no-stock")}
        </Tag>
      </Wrapper>
    )

  const TagList = []

  if (parcel)
    TagList.push(
      <Tag key="parcel" color="#c162f9">
        {t("car.tags.parcel")}
      </Tag>
    )

  if (isothermal)
    TagList.push(
      <Tag key="isothermal" color="#c162f9">
        {t("car.tags.isothermal")}
      </Tag>
    )

  if (fastDelivering && !noFastDelivering)
    TagList.push(
      <Tag key="fast_delivering" color="#0433bf">
        {t("car.tags.fast")}
      </Tag>
    )

  if (sellingStatus === "flexible" && flexibleStatus) {
    TagList.push(
      <Tag key="availability" outline color="#ffffff">
        {t(`car.tags.flexible_status.${flexibleStatus}`)}
      </Tag>
    )

    if (hasAllRiskInsurance) {
      TagList.push(
        <Tag key="availability"  color="#0433bf">
          {t(`car.tags.hasInsurance`)}
        </Tag>
      )
    } else {
      TagList.push(
        <Tag key="availability"  color="#0433bf">
          {t(`car.tags.hasNotInsurance`)}
        </Tag>
      )
    }
  }

  if (sellingStatus === "pre-owned") {
    TagList.push(
      <Tag key="pre-owned" color="#0433BF">
        {t("car.tags.pre-owned")}
      </Tag>
    )

    TagList.push(
      <Tag
        key="availability"
        outline
        color="#ffffff"
        dangerouslySetInnerHTML={{
          __html: t("car.tags.available.or", { first: t("car.tags.available.freelance"), second: t("car.tags.available.employee") })
        }}
      />
    )
  }

  if (lastItems)
    TagList.push(
      <Tag key="last_items" color="#54d6a6">
        {t("car.tags.last")}
      </Tag>
    )

  if (sellingStatus !== "pre-owned") {
    const availability = []
    if (availableForCompany) availability.push(t("car.tags.available.company"))
    if (availableForFreelance) availability.push(t("car.tags.available.freelance"))
    if (availableForEmployee) availability.push(t("car.tags.available.employee"))

    if (availability.length === 1)
      TagList.push(
        <Tag key="availability" outline color="#ffffff">
          {t("car.tags.available.only", { tag: availability[0] })}
        </Tag>
      )
    else if (availability.length === 2)
      TagList.push(
        <Tag
          key="availability"
          outline
          color="#ffffff"
          dangerouslySetInnerHTML={{
            __html: t("car.tags.available.or", { first: availability[0], second: availability[1] })
          }}
        />
      )
  }

  const list = limit ? TagList.slice(-limit) : TagList
  if (reverse) list.reverse()

  return <Wrapper>{list}</Wrapper>
}

TagsList.propTypes = {
  fastDelivering: PropTypes.bool,
  isothermal: PropTypes.bool,
  lastItems: PropTypes.bool,
  limit: PropTypes.number,
  parcel: PropTypes.bool,
  sellingStatus: PropTypes.oneOf(["pre-owned", "used", "km0", "new", "flexible"]),
  sold: PropTypes.bool,
  availableForEmployee: PropTypes.bool,
  availableForCompany: PropTypes.bool,
  availableForFreelance: PropTypes.bool,
  reverse: PropTypes.bool,
  noFastDelivering: PropTypes.bool,
  flexibleStatus: PropTypes.oneOf(["new", "used"]),
  hasAllRiskInsurance: PropTypes.bool
}

TagsList.defaultProps = {
  limit: 0,
  fastDelivering: false,
  isothermal: false,
  lastItems: false,
  parcel: false,
  sellingStatus: null,
  sold: false,
  availableForEmployee: true,
  availableForCompany: true,
  availableForFreelance: true,
  reverse: false,
  noFastDelivering: false,
  flexibleStatus: null,
  hasAllRiskInsurance: false
}
